import {Controller} from "stimulus";
import getStripeErrorMessage from "../../lib/stripe_errors";

export default class extends Controller {
  static targets = ["creditCardInfo", "upgPaymentForm", "upgCardElement", "upgSubmit", "upgErrorElement",
                    "upgSubmitText", "upgSpinner"]

  connect() {
    this.clientSecret = this.data.get("client-secret");
    this.publishableKey = this.data.get("publishable-key");
    this.pollInterval = null;

    if (!this.clientSecret || !this.publishableKey) {
      this.pollForGatewayKeys();
    } else {
      this.setUpStripe();
    }

    // Start polling for session status every minute
    this.startSessionPolling();
  }

  disconnect() {
    this.stopSessionPolling();
  }

  startSessionPolling() {
    this.pollInterval = setInterval(async () => {
      try {
        const response = await fetch(this.data.get('setup-intent-session-url'));
        if (response.ok) {
          const data = await response.json();
          if (!data.session_status.valid) {
            clearInterval(this.pollInterval);
            alert("Your session has expired.  This page will reload to restart the session.");
            window.parent.location.reload();
          }
        } else {
          console.error("Failed to check session status:", response.statusText);
        }
      } catch (error) {
        console.error("Error checking session status:", error);
      }
    }, 30000); // Poll every 60 seconds
  }

  stopSessionPolling() {
    if (this.pollInterval) {
      clearInterval(this.pollInterval);
    }
  }
                  
  pollForGatewayKeys() {
    const url = this.data.get('setup-intent-url');
    const startTime = Date.now();

    this.pollingInterval = setInterval(async () => {
      try {
        const response = await fetch(url);
        if (response.ok) {
          clearInterval(this.pollingInterval);
          const data = await response.json();
          this.clientSecret = data.client_secret; // Store the clientSecret
          this.publishableKey = data.publishable_key;
          this.setUpStripe();
        } else if (Date.now() - startTime >= this.pollDuration) {
          clearInterval(this.pollingInterval);
          console.error("Fetch setup intent timed out.");
          this.showError("Polling timed out. Please try again later.");
        }
      } catch (error) {
        console.error("Polling error:", error);
        if (Date.now() - startTime >= this.pollDuration) {
          clearInterval(this.pollingInterval);
          this.showError("Polling failed. Please check your connection.");
        }
      }
    }, 1000);
  }

  setUpStripe() {
    let stripe = Stripe(this.publishableKey);
    stripe.retrieveSetupIntent(this.clientSecret).then((result) => {
      if (result.error) {
        console.error("Error retrieving setup intent:", result.error);
        this.showError(result.error.message);
        return;
      }

      const setupIntent = result.setupIntent;
      console.log(setupIntent)
      console.log("Setup Intent Status:", setupIntent.status);

      if (setupIntent.status !== "requires_payment_method") {
        alert("Your payment session has invalid.  This page will reload to restart the session.");
        this.deleteSessionAndReload();
      } else {
        let card = this.mountCard(stripe);
        this.addSubmitHandler(stripe, card);
      }
    });
  }

  async deleteSessionAndReload() {
    try {
      const response = await fetch(this.data.get('setup-intent-url'), {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }

      console.log("Session deleted successfully");
    } catch (error) {
      console.error("Error deleting session:", error);
    } finally {
      window.location.reload();
    }
  }

  mountCard(stripe) {
    // Disable the button until we have Stripe set up on the page
    this.upgSubmitTarget.disabled = true;

    let style = {
      base: {
        color: "#32325d",
        fontFamily: 'Arial, sans-serif',
        fontSmoothing: "antialiased",
        fontSize: "16px",
        "::placeholder": {
          color: "#32325d"
        }
      },
      invalid: {
        fontFamily: 'Arial, sans-serif',
        color: "#fa755a",
        iconColor: "#fa755a"
      }
    };

    let card = stripe.elements().create("card", {style: style});
    card.mount(this.upgCardElementTarget);
    card.on("change", this.changeCard.bind(this));

    return card;
  }

  changeCard(event) {
    // Disable the Pay button if there are no card details in the Element
    this.upgSubmitTarget.disabled = event.empty;
    this.upgErrorElementTarget.textContent = event.error ? event.error.message : "";
  }

  addSubmitHandler(stripe, card) {
    let form = this.upgPaymentFormTarget;
    form.addEventListener("submit", this.handleSubmit(stripe, card).bind(this));
  }

  handleSubmit(stripe, card) {
    return function (event) {
      event.preventDefault();
      this.stopSessionPolling(); // Stop polling when form is submitted
      this.setUpCard(stripe, card);
    };
  }

  setUpCard(stripe, card) {
    this.loading(true);
    stripe
      .confirmCardSetup(this.clientSecret, {
        payment_method: {
          card: card
        }
      })
      .then(this.handlePaymentResult().bind(this));

  };

  handlePaymentResult() {
    return function (result) {
      if (result.error) {
        console.log('Declined',result.error)
        this.showError(getStripeErrorMessage(result.error.decline_code));
        this.logPaymentFailure(result.error);
      } else {
        this.completeSetUp(result.setupIntent.id);
      }
    };
  }

  // Show the customer the error from Stripe if their card fails to charge
  showError(errorMsgText) {
    this.loading(false);

    let errorMsg = this.upgErrorElementTarget;

    // Stripe uses the word "postal" instead of "zip code" in error messages,
    // which has the potential to confuse customers. So we replace "postal" here.
    let fixedErrorMsgText = errorMsgText.replace(/postal/i, 'zip code');

    errorMsg.textContent = fixedErrorMsgText;
    setTimeout(function() {
      errorMsg.textContent = "";
    }, 12000);
  };

  // Shows a success message when the payment is complete
  completeSetUp(setupIntentId) {
    this.loading(false);
    this.upgSubmitTarget.disabled = true;
    document.getElementById("setupIntentId").value = setupIntentId
    console.log('setupIntentId',setupIntentId);

    const confirmUrl = this.data.get('confirm-setup-intent-url'); // Get the URL from the data attribute

    fetch(confirmUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({ setup_intent_id: setupIntentId })
    })
    .then(response => {
      if (!response.ok) {
        this.showError("Failed to confirm payment setup. Please try again.");
        throw new Error(`Server error: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      console.log("Setup intent confirmed successfully:", data);
      document.getElementById("cardId" ).value = data.card.id;
      document.getElementById("cardLast4Digits" ).value = data.card.last4;
      document.getElementById("cardType" ).value = data.card.type;
      document.getElementById("cardExpirationDate" ).value = data.card.exp_date;
      document.getElementById("paymentForm").submit();
    })
    .catch(error => {
      console.error("Error confirming setup intent:", error);
      this.showError("Failed to confirm payment setup. Please try again.");
    });
 
  };

  // Show a spinner on payment submission
  loading(isLoading) {
    if (isLoading) {
      // Disable the button and show a spinner
      this.upgSubmitTarget.disabled = true;
      this.upgSpinnerTarget.classList.remove("hidden");
      this.upgSubmitTextTarget.classList.add("hidden");
    } else {
      this.upgSubmitTarget.disabled = false;
      this.upgSpinnerTarget.classList.add("hidden");
      this.upgSubmitTextTarget.classList.remove("hidden");
    }
  };

  deleteSession() {
    const sessionUrl = this.data.get('setup-intent-session-url');
  
    fetch(sessionUrl, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      console.log("Session deleted successfully:", data);
      // Perform any additional actions here, such as redirecting or showing a success message
    })
    .catch(error => {
      console.error("Error deleting session:", error);
      this.showError("Failed to delete session. Please try again.");
    });
  }

  logPaymentFailure(error) {
    const logUrl = this.data.get('log-payment-failure-url'); // Ensure this data attribute is set in your HTML
  
    fetch(logUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({
        code: error.code,
        decline_code: error.decline_code,
        message: error.message,
        type: error.type,
        card: {
          brand: error?.payment_method?.card?.brand,
          last4: error?.payment_method?.card?.last4,
          exp_month: error?.payment_method?.card?.exp_month,
          exp_year: error?.payment_method?.card?.exp_year,
        }
      })
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Server error: ${response.statusText}`);
      }
      return response.json();
    })
    .then(data => {
      console.log("Payment failure logged successfully:", data);
    })
    .catch(error => {
      console.error("Error logging payment failure:", error);
    });
  }
  
  
}
